/**
 * Leviathan Web - Past Due Invoices Widget
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows past due count + total amount from AR summary
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle } from 'lucide-react';
import { API_URL } from '@/config/api';

interface ArSummary {
  totalOutstanding: number;
  totalOverdue: number;
  overdueCount: number;
  agingBuckets?: {
    current: number;
    thirtyDay: number;
    sixtyDay: number;
    ninetyDay: number;
    overNinety: number;
  };
}

const PastDueInvoicesWidget = () => {
  const { accessToken } = useAuth();
  const [data, setData] = useState<ArSummary | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/ar-summary`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const json = await response.json();
          setData(json);
        }
      } catch {
        // silent
      }
    };
    fetchData();
  }, [accessToken]);

  const formatCurrency = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const overdueCount = data?.overdueCount ?? 0;
  const overdueAmount = data?.totalOverdue ?? 0;
  const hasOverdue = overdueCount > 0;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center space-x-2 mb-3">
        <AlertTriangle className={`w-4 h-4 ${hasOverdue ? 'text-error-red' : 'text-success-green'}`} />
        <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
          Past Due Invoices
        </span>
      </div>

      <div
        className={`text-3xl font-mono font-bold mb-1 ${hasOverdue ? 'text-error-red' : 'text-success-green'}`}
        style={{
          textShadow: hasOverdue
            ? '0 0 15px rgba(239,68,68,0.6)'
            : '0 0 15px rgba(74,222,128,0.6)',
        }}
      >
        {overdueCount}
      </div>
      <div className="text-text-tertiary text-xs font-mono mb-3">
        {hasOverdue ? formatCurrency(overdueAmount) + ' overdue' : 'All current'}
      </div>

      {data?.agingBuckets && (
        <div className="space-y-1.5 mt-auto">
          <div className="flex justify-between text-[10px] font-mono">
            <span className="text-text-tertiary">Outstanding</span>
            <span className="text-amber">{formatCurrency(data.totalOutstanding)}</span>
          </div>
        </div>
      )}

      <Link
        to="/invoices"
        className="mt-auto pt-2 text-[10px] font-mono text-text-tertiary hover:text-amber transition-colors text-center block"
      >
        View Invoices &rarr;
      </Link>
    </div>
  );
};

export default PastDueInvoicesWidget;
